// TopologyView is a thin router that dispatches to one of four
// mode-specific renderers. Summary / Subnet / All Hosts render in
// plain HTML. the old circular Cytoscape view squashed 100+ host icons
// into a tiny disk that was unreadable in practice. only Attack Path
// still uses Cytoscape, and only when there's a real attack subgraph
// to draw.
//
// the route component owns *no* layout state. positions, filters and
// selection live in the leaf views. keeps mode-switching cheap (no
// remount cascade) without the position-once gymnastics of the prior
// cytoscape-everywhere design

import { forwardRef, useImperativeHandle, useRef } from 'react'
import type {
  EngagementAsset,
  EngagementAttackRun,
  EngagementEvidence,
  EngagementFinding,
  EngagementPort,
  EngagementSubnet,
  TopologySnapshot,
} from '@/lib/engagementsApi'
import { SummaryCards } from './topologyModes/SummaryCards'
import { SubnetGridView } from './topologyModes/SubnetGridView'
import { AllHostsGrid } from './topologyModes/AllHostsGrid'
import { AttackPathCanvas, type AttackPathCanvasHandle } from './topologyModes/AttackPathCanvas'

export type TopologyViewMode = 'summary' | 'subnet' | 'attack' | 'all'

export interface TopologyViewHandle {
  zoomIn:  () => void
  zoomOut: () => void
  fit:     () => void
}

interface Props {
  snapshot:   TopologySnapshot | null
  assets:     EngagementAsset[]
  ports:      EngagementPort[]
  subnets:    EngagementSubnet[]
  attackRuns: EngagementAttackRun[]
  findings?:  EngagementFinding[]
  evidence?:  EngagementEvidence[]
  mode:       TopologyViewMode
  onSelect:   (data: Record<string, any> | null) => void
}

// handle is wired through so the floating CanvasToolbar buttons stay
// functional in Attack Path mode. the HTML modes are no-ops, they have
// no zoomable surface and the toolbar is hidden in those modes anyway
export const TopologyView = forwardRef<TopologyViewHandle, Props>(
  ({ snapshot, assets, ports, subnets, attackRuns, findings, evidence, mode, onSelect }, ref) => {
    const attackRef = useRef<AttackPathCanvasHandle>(null)

    useImperativeHandle(ref, () => ({
      zoomIn:  () => attackRef.current?.zoomIn(),
      zoomOut: () => attackRef.current?.zoomOut(),
      fit:     () => attackRef.current?.fit(),
    }), [])

    if (mode === 'summary') {
      return (
        <SummaryCards
          assets={assets}
          subnets={subnets}
          ports={ports}
          attackRuns={attackRuns}
          findings={findings ?? []}
          onSelect={onSelect}
        />
      )
    }

    if (mode === 'subnet') {
      return (
        <SubnetGridView
          assets={assets}
          subnets={subnets}
          ports={ports}
          attackRuns={attackRuns}
          findings={findings ?? []}
          evidence={evidence ?? []}
          onSelect={onSelect}
        />
      )
    }

    if (mode === 'attack') {
      return (
        <AttackPathCanvas
          ref={attackRef}
          assets={assets}
          attackRuns={attackRuns}
          snapshot={snapshot}
          onSelect={onSelect}
        />
      )
    }

    // mode === 'all'
    return (
      <AllHostsGrid
        assets={assets}
        ports={ports}
        attackRuns={attackRuns}
        findings={findings ?? []}
        evidence={evidence ?? []}
        onSelect={onSelect}
      />
    )
  }
)
TopologyView.displayName = 'TopologyView'
